import React, { useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";
import CustomTooltip from "../token/Tooltip";
import { StatBox } from "../token/StatsBox";
import type { SaleData } from "../token/StatsBox";

interface DashboardVentesProps {
  data: SaleData[];
}
const DashboardVentes: React.FC<DashboardVentesProps> = ({ data }) => {
  const processedSales = data.map((p) => ({
    ...p,
    year: p.paymentDate?.getFullYear() ?? null,
    month: p.paymentDate?.getMonth() ?? null,
  }));

  const today = new Date();
  const currentYear = today.getFullYear();

  // ── Compute total and average for current year ────────────────────
  const totalThisYear = processedSales
    .filter((p) => p.year === currentYear)
    .reduce((sum, p) => sum + p.amount, 0);
  // Nombre de mois écoulés dans l'année (Janvier = 1, …)
  const monthsElapsed = today.getMonth() + 1;
  const averageMonthly = monthsElapsed > 0 ? totalThisYear / monthsElapsed : 0;

  // ── Common labels ────────────────────────────────────────────────
  const monthLabels = [
    "Janv.",
    "Fév.",
    "Mars",
    "Avril",
    "Mai",
    "Juin",
    "Juillet",
    "Août",
    "Sept.",
    "Oct.",
    "Nov.",
    "Déc.",
  ];

  // ── Monthly data ────────────────────────────────────────────────
  const dataMonthly = monthLabels.map((label, idx) => ({
    name: label,
    value: processedSales
      .filter(
        (p) =>
          p.startDate?.getFullYear() === currentYear &&
          p.startDate.getMonth() === idx
      )
      .reduce((sum, p) => sum + p.amount, 0),
  }));
  // Log données pour Mars et Avril
  console.log("Données Mars:", dataMonthly[2]);
  console.log("Données Avril:", dataMonthly[3]);
  // Réunir et loguer tous les projets pris en compte pour Mars et Avril
  console.log(
    "Projets Mars:",
    processedSales.filter(
      (p) =>
        p.startDate?.getFullYear() === currentYear &&
        p.startDate.getMonth() === 2
    )
  );
  console.log(
    "Projets Avril:",
    processedSales.filter(
      (p) =>
        p.startDate?.getFullYear() === currentYear &&
        p.startDate.getMonth() === 3
    )
  );

  // ── Quarterly data ──────────────────────────────────────────────
  const quarterRanges = [
    { name: "Q1", months: [0, 1, 2] },
    { name: "Q2", months: [3, 4, 5] },
    { name: "Q3", months: [6, 7, 8] },
    { name: "Q4", months: [9, 10, 11] },
  ];
  const dataQuarterly = quarterRanges.map(({ name, months }) => ({
    name,
    value: processedSales
      .filter(
        (p) =>
          p.startDate?.getFullYear() === currentYear &&
          p.startDate.getMonth() !== null &&
          months.includes(p.startDate.getMonth())
      )
      .reduce((sum, p) => sum + p.amount, 0),
  }));

  // ── Yearly data ────────────────────────────────────────────────
  const years = Array.from(
    new Set(
      processedSales.map((p) => p.year).filter((y): y is number => y !== null)
    )
  ).sort();
  const dataYearly = years.map((y) => ({
    name: String(y),
    value: processedSales
      .filter((p) => p.startDate?.getFullYear() === Number(y))
      .reduce((sum, p) => sum + p.amount, 0),
  }));

  return (
    <div className="w-full flex gap-6">
      <div className="flex flex-col gap-6 w-full h-full">
        <StatBox
          label="Chiffre d’affaire mensuel moyen"
          value={averageMonthly.toLocaleString("fr-FR", {
            style: "currency",
            currency: "EUR",
          })}
        />

        {/* Graphique mensuel */}
        <div className="w-full bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex flex-col h-full">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Devis signés (mensuel)
          </h3>
          <div className="flex-grow">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={dataMonthly}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#F1F1F1"
                  vertical={false}
                />
                <XAxis dataKey="name" interval={0} />
                <YAxis
                  domain={[0, "dataMax"]}
                  tickFormatter={(v) =>
                    v >= 1000
                      ? `${(v / 1000).toFixed(1)}k €`
                      : `${v.toLocaleString("fr-FR")} €`
                  }
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="value" fill="#99C3FF" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-6 w-full h-full">
        {/* Trimestriel */}
        <div className="w-full bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex flex-col h-full">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Devis signés (trimestriel)
          </h3>
          <div className="flex-grow">
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={dataQuarterly}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#F1F1F1"
                  vertical={false}
                />
                <XAxis dataKey="name" />
                <YAxis
                  domain={[0, "dataMax"]}
                  tickFormatter={(v) =>
                    v >= 1000
                      ? `${(v / 1000).toFixed(1)}k €`
                      : `${v.toLocaleString("fr-FR")} €`
                  }
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="value" fill="#99C3FF" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        {/* Annuel */}
        <div className="w-full bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex flex-col h-full">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Devis signés (annuel)
          </h3>
          <div className="flex-grow">
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={dataYearly}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#F1F1F1"
                  vertical={false}
                />
                <XAxis dataKey="name" />
                <YAxis
                  domain={[0, "dataMax"]}
                  tickFormatter={(v) =>
                    v >= 1000
                      ? `${(v / 1000).toFixed(1)}k €`
                      : `${v.toLocaleString("fr-FR")} €`
                  }
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="value" fill="#99C3FF" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardVentes;
